<template>
  <nav class="sidebar-menu">
    <div v-if="is_general_manager" class="sidebar-menu__search">
      <input v-model="filter" type="text" placeholder="Search" class="sidebar-menu__search-input">
    </div>
    <div class="sidebar-menu__wrapper">
      <template v-for="part in menuFiltered">
        <div
          v-if="showMenuSection(part) && (!is_alarm_status || (is_alarm_status && !part.alarm))"
          :key="part.title"
          class="sidebar-menu__part"
        >
          <div class="sidebar-menu__title">{{ part.title }}</div>
          <div class="sidebar-menu__list" :key="temp_key">
            <router-link
              v-for="item in part.list"
              :key="item.label"
              v-show="showMenuItem(item)"
              :to="{ name: item.name === 'safari-snack-bar' ? snackbarHome(groups) : item.name, params: item.params }"
              class="sidebar-menu__item"
              active-class="sidebar-menu__item--active"
              :class="{ 'sidebar-menu__item--counted': item.count }"
            >
              {{ item.label }} <span v-if="item.count" class="sidebar-menu__count">{{ item.count }}</span>
            </router-link>
          </div>
        </div>
      </template>
    </div>
  </nav>
</template>

<script>
import { getNavStats } from '@/http'
import { allowsAccess } from '@/utils/permissionAccess'
import { techSupportAccess } from '@/utils/techSupportAccess'
import { groundOpsAccess } from '@/utils/groundOpsAccess'
import { snackbarHome } from '@/utils/safariRefundAccess'
import { flightBookingRoles } from '@/utils/flightBookingAccess'

export default {
  name: 'AppSidebarMenu',
  props: {
    groups: Array,
  },
  data() {
    return {
      is_alarm_status: process.env.VUE_APP_ALARM === 'true',
      filter: '',
      real_menu: [
        {
          title: 'CRM',
          list: [
            { label: 'Leads', name: 'leads-list', roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'marketing_supervisor', 'support'] },
            { label: 'Lead Flow', name: 'crm-task-tracker', roles: ['general_manager', 'marketing_supervisor', 'sales_supervisor', 'lead_new_flow'] },
            { label: 'Follow-up Automation', name: 'crm-follow-up-automation', roles: ['general_manager', 'sales_supervisor', 'marketing_supervisor'] },
            { label: 'Allowance', name: 'allowance-list', roles: ['general_manager'] },
            { label: 'Analytics', name: 'crm-analytics-requests', roles: ['general_manager', 'marketing_assistant', 'marketing_supervisor'] },
            { label: 'Trengo', name: 'crm-trengo-matching', roles: ['general_manager', 'marketing_supervisor'] }
          ],
          roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'marketing_assistant', 'marketing_supervisor', 'support', 'lead_new_flow'],
          alarm: true
        },
        {
          title: 'Clients',
          list: [
            { label: 'Groups', name: 'clients-groups', roles: ['general_manager', 'sales_manager', 'operations_manager', 'sales_supervisor', 'operations_supervisor', 'support', 'finance_manager'] },
            { label: 'Free days', name: 'free-days-list', roles: ['general_manager', 'sales_manager', 'operations_manager', 'sales_supervisor', 'operations_supervisor', 'support'] },
            { label: 'Authorization forms', name: 'authorization-forms-pending', roles: ['general_manager', 'operations_supervisor', 'finance_manager'] },
            { label: 'Payment control', name: 'payment-control-groups', roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'finance_manager'] },
            { label: 'Dashboard', name: 'clients-dashboard', roles: ['general_manager', 'sales_manager', 'sales_supervisor'] },
            { label: 'Invoices', name: 'clients-invoices', roles: ['general_manager', 'sales_supervisor'] },
            { label: 'Arrivals', name: 'clients-arrivals', roles: ['general_manager', 'operations_supervisor'] },
            { label: 'Analytics', name: 'clients-analytics', roles: ['general_manager', 'marketing_supervisor'] },
          ],
          roles: ['general_manager', 'sales_manager', 'operations_manager', 'sales_supervisor', 'operations_supervisor', 'support', 'marketing_supervisor', 'finance_manager'],
          alarm: false
        },
        {
          title: 'Climbing',
          list: [
            { label: 'Groups', name: 'climbing-groups', roles: ['general_manager', 'sales_manager', 'operations_manager', 'climbing_manager', 'climbing_supervisor', 'climbing_assistant', 'sales_supervisor', 'operations_supervisor', 'support'] },
            { label: 'My Team', name: 'climbing-my-team', roles: ['general_manager', 'climbing_manager', 'climbing_supervisor', 'climbing_assistant'] },
            { label: 'Groups supply', name: 'climbing-groups-supply', roles: ['general_manager', 'climbing_supervisor'] },
            { label: 'Groups crew', name: 'climbing-groups-crew', roles: ['general_manager', 'climbing_supervisor'] },
            { label: 'Birthdays', name: 'climbing-birthdays', roles: ['general_manager', 'operations_manager', 'climbing_manager', 'climbing_supervisor', 'climbing_assistant', 'operations_supervisor'] },
            { label: 'Briefings', name: 'climbing-briefings-list', roles: ['general_manager', 'sales_manager', 'operations_manager', 'operations_supervisor', 'finance_manager'] },
            { label: 'Arrivals', name: 'climbing-arrivals-list', roles: ['general_manager', 'operations_supervisor', 'operations_manager'] },
            { label: 'Mountain crew', name: 'climbing-mountain-crew', roles: ['general_manager', 'climbing_supervisor'] },
            { label: 'Analytics', name: 'climbing-analytics-general', roles: ['general_manager', 'climbing_supervisor'] },
            { label: 'Settings', name: 'climbing-settings-gps', roles: ['general_manager', 'climbing_supervisor'] },
          ],
          roles: ['general_manager', 'sales_manager', 'operations_manager', 'climbing_manager', 'climbing_supervisor', 'climbing_assistant', 'sales_supervisor', 'operations_supervisor', 'support', 'finance_manager'],
          alarm: true
        },
        {
          title: 'Safari',
          list: [
            { label: 'Groups', name: 'safari-groups', roles: ['general_manager', 'sales_manager', 'operations_manager', 'safari_supervisor', 'safari_manager', 'safari_assistant', 'sales_supervisor', 'operations_supervisor', 'support'] },
            { label: 'Snack bar', name: 'safari-snack-bar', roles: ['general_manager', 'finance_manager', 'accountant_manager', 'accountant_supervisor', 'sales_manager', 'sales_supervisor', 'safari_supervisor', 'safari_manager', 'safari_assistant', 'operations_manager', 'operations_supervisor'] },
            { label: 'Schedule', name: 'safari-schedule-list', roles: ['general_manager', 'safari_supervisor', 'safari_manager', 'safari_assistant', 'sales_manager', 'sales_supervisor'] },
            { label: 'Cars & Drivers', name: 'safari-cars-drivers', roles: ['general_manager', 'safari_supervisor'] },
            { label: 'Allowance', name: 'safari-allowance-list', roles: ['general_manager', 'safari_supervisor', 'safari_manager', 'safari_assistant', 'hr_supervisor'] },
            { label: 'Analytics', name: 'safari-analytics-general', roles: ['general_manager', 'safari_supervisor'] },
            { label: 'Flying doctors', name: 'safari-doctors-list-upcoming', roles: ['safari_flying_doctors'] },
          ],
          roles: ['general_manager', 'sales_manager', 'operations_manager', 'safari_supervisor', 'safari_manager', 'safari_assistant', 'safari_flying_doctors', 'sales_supervisor', 'operations_supervisor', 'support', 'hr_supervisor', 'finance_manager', 'accountant_manager', 'accountant_supervisor'],
          alarm: true
        },
        {
          title: 'Day trips',
          list: [ { label: 'Groups', name: 'day-trips-groups-list', roles: ['general_manager', 'sales_manager', 'operations_manager', 'sales_supervisor', 'operations_supervisor', 'support'] } ],
          roles: ['general_manager', 'sales_manager', 'operations_manager', 'sales_supervisor', 'operations_supervisor', 'support'],
          alarm: true
        },
        {
          title: 'Island vacation',
          list: [ { label: 'Groups', name: 'island-vacation-groups-list', roles: ['general_manager', 'sales_manager', 'operations_manager', 'sales_supervisor', 'operations_supervisor', 'support'] } ],
          roles: ['general_manager', 'sales_manager', 'operations_manager', 'sales_supervisor', 'operations_supervisor', 'support'],
          alarm: true
        },
        {
          title: 'Reservation',
          list: [
            { label: 'Hotels', name: 'reservation-hotels-list', roles: ['general_manager', 'reservation_supervisor', 'reservation_manager', 'reservation_assistant', 'sales_manager', 'sales_supervisor', 'support'] },
            { label: 'Meals', name: 'reservation-meals-upcoming', roles: ['general_manager', 'reservation_supervisor', 'reservation_manager', 'reservation_assistant', 'sales_manager', 'sales_supervisor', 'support'] },
            { label: 'Flights', name: 'reservation-flights-list', roles: ['general_manager'] },
            { label: 'Safari', name: 'reservation-safari-list', roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'safari_supervisor'] },
            { label: 'Bed in twin', name: 'reservation-bed-in-twin', roles: ['general_manager', 'reservation_supervisor', 'reservation_manager', 'reservation_assistant', 'sales_manager', 'sales_supervisor', 'support'] },
            { label: 'Payment requests', name: 'reservation-payment-requests', roles: ['general_manager', 'reservation_supervisor'] },
            { label: 'Cancellation', name: 'reservation-hotels-cancellations-active', roles: ['general_manager', 'reservation_supervisor', 'reservation_manager', 'reservation_assistant', 'sales_manager', 'sales_supervisor'] },
            { label: 'Amendments', name: 'reservation-hotels-amendments-pending', roles: ['general_manager', 'reservation_supervisor', 'reservation_manager', 'reservation_assistant', 'sales_manager', 'sales_supervisor'] },
            { label: 'Dashboard', name: 'reservation-dashboard', roles: ['general_manager', 'reservation_supervisor', 'reservation_manager', 'reservation_assistant'] },
            { label: 'Analytics', name: 'reservation-analytics', roles: ['general_manager'] },
          ],
          roles: ['general_manager', 'reservation_supervisor', 'reservation_manager', 'reservation_assistant', 'sales_manager', 'sales_supervisor', 'support', 'safari_supervisor'],
          alarm: false
        },
        {
          title: 'Flights',
          list: [
            { label: 'Aviasales', name: 'flights-aviasales', roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'reservation_supervisor', 'reservation_manager'] },
            { label: 'Bookings', name: 'flights-bookings', roles: flightBookingRoles }
          ],
          roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'reservation_supervisor', 'reservation_manager'],
          alarm: false
        },
        {
          title: 'Reservations DB',
          list: [
            { label: 'Hotel selection', name: 'hotel-map', roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'safari_supervisor'] },
            { label: 'Flights selection', name: 'reservation-db-flights', roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'safari_supervisor'] },
            { label: 'Database', name: 'hotels-database-list', roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'safari_supervisor'] },
          ],
          roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'safari_supervisor'],
          alarm: false
        },
        {
          title: 'Storage',
          list: [
            { label: 'Product storage', name: 'storage-product-storage-orders', roles: ['general_manager'] },
            { label: 'Climbing Equipment', name: 'storage-climbing-equipment', roles: ['general_manager'] },
            { label: 'Kitchen Equipment', name: 'storage-kitchen-equipment', roles: ['general_manager'] },
            { label: 'Climbing Electronics', name: 'storage-climbing-electronics', roles: ['general_manager'] },
            { label: 'Medications & Equipment', name: 'storage-medications-equipment', roles: ['general_manager'] },
            { label: 'Oxygen & Gas', name: 'storage-oxygen-gas', roles: ['general_manager'] },
            { label: 'Suppliers Prices', name: 'storage-supplier-prices', roles: ['general_manager'] },
            { label: 'Items', name: 'storage-items', roles: ['general_manager'] },
          ],
          roles: ['general_manager'],
          alarm: false
        },
        {
          title: 'Product storage',
          list: [
            { label: 'Orders', name: 'product-storage-orders', roles: ['general_manager', 'product_storage_manager', 'product_storage_storekeeper', 'product_storage_supervisor'] },
            { label: 'Cash requests', name: 'product-storage-cash-active', roles: ['general_manager', 'product_storage_manager', 'product_storage_storekeeper', 'product_storage_supervisor'] },
            { label: 'Transfers', name: 'product-storage-transfers', roles: ['general_manager', 'product_storage_manager', 'product_storage_storekeeper', 'product_storage_supervisor'] },
            { label: 'Products', name: 'product-storage-products-list', roles: ['general_manager', 'product_storage_manager', 'product_storage_storekeeper', 'product_storage_supervisor'] },
            { label: 'Suppliers', name: 'suppliers-list', roles: ['general_manager', 'product_storage_manager', 'product_storage_storekeeper', 'product_storage_supervisor'] },
            { label: 'Stocktaking', name: 'product-storage-stocktaking-list', roles: ['general_manager', 'product_storage_manager', 'product_storage_storekeeper', 'product_storage_supervisor'] },
            { label: 'Analytics', name: 'product-storage-analytics-general', roles: ['general_manager', 'product_storage_supervisor'] },
          ],
          roles: ['general_manager', 'product_storage_manager', 'product_storage_storekeeper', 'product_storage_supervisor'],
          alarm: false
        },
        {
          title: 'Equipment Storage',
          list: [
            { label: 'Rentals', name: 'rentals-active', roles: ['general_manager', 'equipment_storage_manager', 'equipment_storage_storekeeper'] },
            { label: 'Rental items', name: 'rental-items-active', roles: ['general_manager', 'equipment_storage_manager', 'equipment_storage_storekeeper'] },
            { label: 'Purchase items', name: 'purchase-items', roles: ['general_manager', 'equipment_storage_manager', 'equipment_storage_storekeeper'] },
          ],
          roles: ['general_manager', 'equipment_storage_manager', 'equipment_storage_storekeeper'],
          alarm: true
        },
        {
          title: 'Transfer',
          list: [
            { label: 'Transfers', name: 'transfers-upcoming', roles: ['general_manager', 'sales_manager', 'transfer_manager', 'transfer_supervisor', 'transfer_assistant', 'sales_supervisor', 'operations_supervisor', 'support', 'hotel_reception', 'hotel_supervisor', 'hotel_reservations'] },
            { label: 'Fast track', name: 'fast-track-upcoming', roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'transfer_manager', 'transfer_supervisor', 'transfer_assistant'] },
            { label: 'Taxi drivers', name: 'taxi-drivers-list', roles: ['general_manager', 'transfer_manager', 'transfer_supervisor', 'transfer_assistant'] },
            { label: 'Rental companies', name: 'rental-companies-list', roles: ['general_manager', 'transfer_manager', 'transfer_supervisor', 'transfer_assistant'] },
            { label: 'Calculator', name: 'transfer-calculator', roles: ['general_manager'] },
            { label: 'Allowance', name: 'transfer-allowance-list', roles: ['general_manager', 'transfer_manager', 'transfer_supervisor', 'transfer_assistant', 'hr_supervisor', 'hr_manager'] },
            { label: 'Schedule', name: 'transfer-schedule-own', roles: ['general_manager', 'transfer_manager', 'transfer_supervisor', 'transfer_assistant'] },
            { label: 'Analytics', name: 'transfer-analytics', roles: ['general_manager'] },
          ],
          roles: ['general_manager', 'sales_manager', 'transfer_manager', 'transfer_supervisor', 'transfer_assistant', 'sales_supervisor', 'operations_supervisor', 'support', 'hr_supervisor', 'hr_manager', 'hotel_reception', 'hotel_supervisor', 'hotel_reservations'],
          alarm: false
        },
        {
          title: 'Car management',
          list: [
            { label: 'Cars', name: 'car-management-cars-list', roles: ['general_manager', 'transfer_manager', 'transfer_supervisor', 'transfer_assistant'] },
            { label: 'Fuel log', name: 'car-management-fuel-log', roles: ['general_manager', 'transfer_manager', 'transfer_supervisor', 'transfer_assistant', 'accountant_supervisor', 'accountant_manager'] },
          ],
          roles: ['general_manager', 'transfer_manager', 'transfer_supervisor', 'transfer_assistant', 'accountant_supervisor', 'accountant_manager'],
          alarm: false
        },
        {
          title: 'Employees',
          list: [
            { label: 'Employees', name: 'employees-list', roles: ['general_manager', 'hr_manager', 'hr_supervisor'] },
            { label: 'Warnings', name: 'employees-warnings', roles: ['general_manager', 'hr_manager', 'hr_supervisor'] },
            { label: 'HESLB Loans', name: 'employees-heslb-loans', roles: ['general_manager', 'hr_manager', 'hr_supervisor', 'accountant_supervisor', 'accountant_manager'] },
            { label: 'Staff Loans', name: 'employees-staff-loans', roles: ['general_manager', 'hr_manager', 'hr_supervisor', 'accountant_supervisor', 'accountant_manager'] },
            { label: 'Advances', name: 'employees-advances', roles: ['general_manager', 'hr_manager', 'hr_supervisor', 'accountant_supervisor', 'accountant_manager'] },
            { label: 'Payroll', name: 'employees-payroll-list', roles: ['general_manager', 'hr_manager', 'hr_supervisor', 'accountant_supervisor', 'accountant_manager'] },
            { label: 'UTT Balance', name: 'employees-utt-balance', roles: ['general_manager'] },
            { label: 'Contract renewals', name: 'employees-contract-renewals', roles: ['general_manager', 'hr_manager', 'hr_supervisor'] },
            { label: 'Employee Feedback', name: 'employees-feedback', roles: ['general_manager'] },
            { label: 'Misconduct', name: 'employees-misconduct', roles: ['general_manager'] },
            { label: 'Dashboard', name: 'employees-dashboard', roles: ['general_manager', 'hr_manager', 'hr_supervisor'] },
            { label: 'Sales Work Schedule', name: 'sales-work-schedule', roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'marketing_supervisor', 'safari_supervisor'] },
            { label: 'Sales Shift Planner', name: 'employees-shift-planner', roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'marketing_supervisor'] },
            { label: 'Leaves', name: 'employees-leaves', roles: ['general_manager', 'hr_manager', 'hr_supervisor'] },
            { label: 'Leave (Sales)', name: 'employees-sales-leaves', roles: ['general_manager', 'sales_supervisor', 'marketing_supervisor'] },
            { label: 'Analytics', name: 'employees-analytics', roles: ['general_manager'] },
          ],
          roles: ['general_manager', 'hr_manager', 'hr_supervisor', 'accountant_supervisor', 'accountant_manager', 'sales_manager', 'sales_supervisor', 'marketing_supervisor', 'safari_supervisor'],
          alarm: false
        },
        {
          title: 'Hotel management',
          list: [
            { label: 'Calendar', name: 'hotel-management-calendar', roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'hotel_reception', 'hotel_reservations', 'reservations_supervisor'] },
            { label: 'My subscriptions', name: 'hotel-management-availability-subscriptions', roles: ['general_manager', 'sales_manager', 'sales_supervisor'] },
            { label: 'Dashboard', name: 'hotel-management-dashboard', roles: ['general_manager', 'hotel_reception'] },
            { label: 'Sauna & Massage', name: 'hotel-management-massage', roles: ['general_manager', 'hotel_reception', 'therapist'] },
            { label: 'Balance', name: 'hotel-management-balance', roles: ['hotel_reception'] },
            { label: 'Reservations', name: 'hotel-management-reservations-list-upcoming', roles: ['general_manager', 'hotel_reservations'] },
            { label: 'House Accounts', name: 'hotel-management-house-accounts', roles: ['general_manager', 'hotel_reception'] },
            { label: 'Restaurant receipts', name: 'hotel-management-restaurant-receipts-guests', roles: ['general_manager', 'hotel_reception'] },
            { label: 'Transactions', name: 'hotel-management-transactions', roles: ['general_manager'] },
            { label: 'Shop stocktaking', name: 'hotel-management-shop-stocktaking', roles: ['general_manager', 'hotel_reception'] },
            { label: 'Balance history', name: 'hotel-management-balance-history', roles: ['general_manager'] },
          ],
          roles: ['general_manager', 'hotel_reception', 'sales_manager', 'sales_supervisor', 'hotel_reservations', 'reservations_supervisor', 'therapist'],
          alarm: true
        },
        {
          title: 'Finance',
          list: [
            { label: 'Client refunds', name: 'finance-refunds', roles: ['general_manager'] },
            { label: 'Cashflow', name: 'finance-cashflow', roles: ['general_manager', 'accountant_supervisor', 'operations_supervisor', 'finance_manager'] },
            { label: 'On-Site payments', name: 'finance-onsite-payments', roles: ['general_manager'] },
            { label: 'Fund requests', name: 'finance-fund-requests-active', roles: ['general_manager', 'accountant_supervisor', 'accountant_manager', 'transfer_supervisor'] },
            { label: 'Supplier invoices', name: 'finance-supplier-invoices', roles: ['general_manager', 'accountant_supervisor', 'accountant_manager'] },
            { label: 'EFD Purchases', name: 'finance-efd-receipts', roles: ['general_manager', 'accountant_supervisor', 'accountant_manager'] },
            { label: 'EFD Sales', name: 'finance-efd-sales', roles: ['general_manager', 'accountant_supervisor'] },
            { label: 'Quick payments', name: 'finance-quick-payments', roles: ['general_manager'] },
            { label: 'POS Transactions', name: 'finance-pos-transactions-pending', roles: ['general_manager', 'finance_manager'] },
            { label: 'Altura Reimbursement', name: 'finance-altura-reimbursement-payments', roles: ['general_manager', 'accountant_supervisor'] },
            { label: 'Cashflow Reconciliation', name: 'finance-cashflow-reconciliation-nmb', roles: ['general_manager', 'accountant_supervisor'] },
            { label: 'Taxes', name: 'finance-taxes', roles: ['general_manager', 'accountant_supervisor'] },
            { label: 'Dictionaries', name: 'finance-dictionaries-banks', roles: ['general_manager'] },
            { label: 'Analytics', name: 'finance-analytics', roles: ['general_manager'] },
          ],
          roles: ['general_manager', 'accountant_supervisor', 'accountant_manager', 'operations_supervisor', 'finance_manager', 'transfer_supervisor'],
          alarm: true
        },
        {
          title: 'Transfer Pricing',
          list: [{ label: 'Funding', name: 'transfer-pricing-funding-payments', roles: ['general_manager'] }],
          roles: ['general_manager'],
          alarm: true
        },
        {
          title: 'Administrative',
          list: [
            { label: 'Suppliers', name: 'administrative-suppliers-list', roles: ['general_manager', 'accountant_supervisor', 'accountant_manager'] },
            { label: 'Legal entities', name: 'administrative-legal-entities-list', roles: ['general_manager', 'accountant_supervisor'] },
            { label: 'Properties', name: 'administrative-properties-list', roles: ['general_manager', 'accountant_supervisor'] },
            { label: 'Domains', name: 'domains', roles: ['general_manager'] },
            { label: 'Land purchase contract', name: 'land-purchase-contract-list', roles: ['general_manager', 'lawyer_manager'] },
          ],
          roles: ['general_manager', 'accountant_supervisor', 'accountant_manager', 'lawyer_manager'],
          alarm: true
        },
        {
          title: 'Common',
          list: [
            { label: 'Dictionaries', name: 'dictionaries', roles: ['general_manager'] },
            { label: 'Users', name: 'users', roles: ['general_manager'] },
            { label: 'Permissions', name: 'users-permissions', roles: ['general_manager'], access: { mode: 'general-manager' } },
            { label: 'Currency exchanger', name: 'currency-exchanger', roles: ['general_manager', 'finance_manager'] },
            { label: 'Signboard', name: 'signboard', roles: ['general_manager', 'hotel_reception', 'operations_supervisor'] },
            { label: 'QR Service', name: 'qr-service', roles: ['general_manager'] },
          ],
          roles: ['general_manager', 'finance_manager'],
          alarm: true
        },
        {
          title: 'Ground Ops',
          list: [
            { label: 'Communication', name: 'ground-ops-communication', roles: ['general_manager'], access: groundOpsAccess('communication') },
            { label: 'Insurance updates', name: 'ground-ops-insurance-updates', roles: ['general_manager', 'support'], access: groundOpsAccess('insurance_updates') }
          ],
          roles: [],
          alarm: false
        },
        {
          title: 'Tech & Support',
          list: [
            { label: 'My tickets', name: 'support-ticket-my-tickets', roles: [], hiddenFor: ['support'], visibleOverride: ['general_manager'] },
            { label: 'Support workspace', name: 'support-ticket-dashboard', roles: ['general_manager', 'support'], access: techSupportAccess('support') },
            { label: 'Releases', name: 'tech-support-releases', roles: ['general_manager', 'support'], access: techSupportAccess('releases') },
            { label: 'Observability', name: 'tech-support-observability', roles: ['general_manager', 'support'], access: techSupportAccess('observability') },
            { label: 'Message Operations', name: 'tech-support-message-operations-telegram', roles: ['general_manager', 'support'], access: techSupportAccess('message_operations') },
            { label: 'Integration Logs', name: 'tech-support-integration-logs-telegram', roles: ['general_manager', 'support'], access: techSupportAccess('integration_logs') },
            { label: 'Trengo', name: 'tech-support-trengo', roles: ['general_manager', 'support'], access: techSupportAccess('trengo') },
            { label: 'Mailgun', name: 'tech-support-mailgun-templates', roles: ['general_manager', 'support'], access: techSupportAccess('mailgun') },
            { label: 'AI Flight Tickets', name: 'tech-support-flight-tickets-relevant', roles: ['general_manager', 'support'], access: techSupportAccess('flight_tickets') },
            { label: 'AI analysis', name: 'tech-support-ai-analysis', roles: ['general_manager', 'support'], access: techSupportAccess('trengo') },
          ],
          roles: [],
          alarm: false
        },
      ],
      nav_stats: {},
      temp_key: 1
    }
  },
  computed: {
    menuFiltered() {
      return this.real_menu.filter(item => {
        return item.list.some(listItem => listItem.label.toLowerCase().includes(this.filter.toLowerCase()))
      })
        .map(item => ({
          ...item,
          list: item.list.filter(listItem => listItem.label.toLowerCase().includes(this.filter.toLowerCase()))
        }))
    },
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
  },
  created() {
    getNavStats().then(res => {
      this.menuFiltered.forEach(menu_item => {
        for (const key in res.data) {
          const temp_obj = menu_item.list.find(item => item.name === key)
          if (temp_obj) temp_obj.count = res.data[key]
        }
      })
      this.temp_key = 2
    })
  },
  methods: {
    snackbarHome,
    showMenuSection(part) {
      if (part.list.some(item => item.access && this.showMenuItem(item))) return true
      if (part.list.every(item => item.access)) return false
      return this.checkRoles(this.groups, part.roles)
    },
    showMenuItem(item) {
      if (item.access) return allowsAccess(item.access, this.$store.state.userData, this.$store.getters['permissions/can'])
      if (!this.checkRoles(this.groups, item.roles)) return false
      const codes = this.groups.map(role => role.code)
      if (item.visibleOverride?.some(role => codes.includes(role))) return true
      return !item.hiddenFor?.some(role => codes.includes(role))
    },
    checkRoles(user_roles, menu_roles) {
      let showMenu = false
      if (!user_roles.length || !menu_roles.length) return true
      user_roles.forEach(role => {
        showMenu ||= menu_roles.some(menu_role => menu_role === role.code)
      })
      return showMenu
    }
  }
}
</script>

<style lang="sass" scoped>
.sidebar-menu
  padding-top: 16px

.sidebar-menu__wrapper
  padding-bottom: 20px

.sidebar-menu__part
  margin-bottom: 15px

  &:last-child
    margin-bottom: 0

.sidebar-menu__title
  margin-bottom: 8px
  padding: 0 20px
  color: #8a8a8a
  font-size: 12px
  font-weight: 600
  text-transform: uppercase

.sidebar-menu__list
  display: flex
  flex-direction: column

.sidebar-menu__item
  padding: 6px 30px 5px
  color: #fff
  font-size: 13px
  font-weight: 500
  transition: background-color 0.3s

  &:hover
    background-color: #515151

.sidebar-menu__item--active
  background-color: #515151

.sidebar-menu__item--counted
  display: flex
  align-items: center
  gap: 6px

.sidebar-menu__search
  margin: 0 16px 20px

.sidebar-menu__search-input
  width: 188px
  height: 32px
  padding: 0 30px 0 14px
  border: 1px solid #757575
  border-radius: 32px
  background-color: #404040
  color: #fff
  font-size: 13px
  background-image: url("~@/assets/img/filter-search-icon.svg")
  background-repeat: no-repeat
  background-position: right 12px center

  &::placeholder
    color: #818181
    font-size: 13px

.sidebar-menu__count
  display: inline-block
  min-width: 24px
  min-height: 18px
  border-radius: 4px
  background-color: $color-theme
  color: #fff
  font-size: 10px
  text-align: center
  line-height: 18px

</style>
